import { AutoCompleteOption } from '../types/AutoCompleteOption';
import { ClientTeamMemberBase } from '../types/ClientTeamMember';

import { buildClientTeamMemberDisplayName } from './buildClientTeamMemberDisplayName';

/**
 * Filters an array of wrapped client team member options based on a search query
 *
 * The filter searches across multiple fields:
 * - Display name (formatted as "EDIPI - LastName, FirstName")
 * - First name
 * - Last name
 * - Semi-unique identifier (EDIPI, DoD ID, or client subgroup ID)
 *
 * @template T Type extending ClientTeamMemberBase
 * @param guests Array of wrapped guest options to filter
 * @param query Search query string (case-insensitive)
 * @returns Filtered array maintaining the wrapper structure
 *
 * @example
 * ```typescript
 * const wrappedGuests = guests.map(g => ({ value: g }));
 * const filtered = filterClientTeamMembers(wrappedGuests, "john");
 * // Returns all guests with "john" in any searchable field
 * ```
 */
export function filterClientTeamMembers<T extends ClientTeamMemberBase>(
  guests: Array<AutoCompleteOption<T>>,
  query: string,
): Array<AutoCompleteOption<T>> {
  // Return all guests if query is empty
  if (!query.trim()) {
    return guests;
  }

  const lowerQuery = query.trim().toLowerCase();

  return guests.filter((option) => {
    const guest = option.value;

    // Search by full display name (includes semi-unique identifier if present)
    const displayName = buildClientTeamMemberDisplayName(guest).toLowerCase();
    if (displayName.includes(lowerQuery)) {
      return true;
    }

    // Also search by individual fields for more flexible matching
    if (guest.firstName.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    if (guest.lastName.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    if (guest.semiUniqueIdentifier?.toLowerCase().includes(lowerQuery)) {
      return true;
    }

    return false;
  });
}
